import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import AnimatedSection from "@/components/AnimatedSection";
import WorkshopCard from "@/components/WorkshopCard";
import {
  ArrowRight,
  CheckCircle2,
  Zap,
  Clock,
  TrendingUp,
  Users,
  Briefcase,
  Building2,
  User,
  FileText,
  MessageSquare,
  Lightbulb,
  Target,
  Shield,
  BookOpen,
  Sparkles,
} from "lucide-react";

const HERO_IMAGE = "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80";
const WORKSHOP_IMAGE = "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80";

const workshops = [
  {
    title: "3-Hour AI Essentials Session",
    duration: "3 hours",
    bestFor: "Individuals, professionals, managers, and business owners who want a strong practical introduction to AI",
    covers: [
      "What AI is and why it matters now",
      "Key AI tools and what they do",
      "Practical ways AI can help in everyday work",
      "How to write better prompts",
      "Safe and responsible use of AI",
      "Real examples for work and business",
    ],
    outcome: "Participants leave with a clear understanding of AI, practical ideas for using it, and more confidence to start applying it in real work.",
  },
  {
    title: "Full-Day Practical AI Workshop",
    duration: "Full day",
    bestFor: "Professionals, teams, and businesses that want deeper practical learning and hands-on use",
    covers: [
      "AI foundations",
      "Practical tools and use cases",
      "Prompt writing in more depth",
      "Using AI for productivity, communication, and planning",
      "Business examples and workflow support",
      "Responsible use and common mistakes to avoid",
      "Hands-on exercises and real workplace scenarios",
    ],
    outcome: "Participants gain a stronger working knowledge of AI and practical skills they can apply immediately in their role or business.",
    featured: true,
  },
];

const whyAIMatters = [
  "AI is becoming part of everyday work across many industries",
  "People who understand AI can often work faster and more effectively",
  "AI can help with writing, research, planning, summaries, and communication",
  "Learning AI now can help people stay relevant as work continues to evolve",
  "Businesses increasingly value people who can use modern tools productively",
  "AI skills can help reduce time spent on repetitive tasks",
  "Knowing how to use AI well can improve confidence with digital tools",
  "Learning AI early gives people more time to build practical capability",
  "Understanding AI also helps people use it more safely and responsibly",
  "The future of work is changing, and now is the time to be ready for it",
];

const benefits = [
  { icon: Clock, text: "Save time on repetitive admin and routine writing tasks" },
  { icon: Zap, text: "Improve the speed of research, brainstorming, and first drafts" },
  { icon: FileText, text: "Create summaries, notes, and action items more efficiently" },
  { icon: MessageSquare, text: "Draft emails, reports, and presentations faster" },
  { icon: Target, text: "Support better planning and clearer communication" },
  { icon: TrendingUp, text: "Increase productivity without adding unnecessary complexity" },
];

const howAIHelps = [
  { icon: Zap, text: "Do research in seconds" },
  { icon: FileText, text: "Generate first drafts faster" },
  { icon: Lightbulb, text: "Create ideas and content quickly" },
  { icon: Clock, text: "Save time on admin tasks" },
  { icon: MessageSquare, text: "Draft messages faster" },
];

const audienceExamples = [
  {
    title: "For Professionals",
    icon: User,
    items: [
      "Drafting emails and responses",
      "Summarising long documents",
      "Preparing reports and presentations",
      "Researching topics faster",
    ],
  },
  {
    title: "For Managers",
    icon: Briefcase,
    items: [
      "Meeting summaries and action points",
      "Briefing notes",
      "Communication drafts",
      "Planning and idea generation",
    ],
  },
  {
    title: "For Businesses",
    icon: Building2,
    items: [
      "Creating internal documentation",
      "Improving team productivity",
      "Drafting customer communication",
      "Generating marketing content ideas",
    ],
  },
  {
    title: "For Small Business Owners",
    icon: Users,
    items: [
      "Writing website copy",
      "Creating service descriptions",
      "Preparing email campaigns",
      "Saving time across day-to-day tasks",
    ],
  },
];

const whyChooseUs = [
  { icon: Target, text: "Practical training for real work" },
  { icon: Users, text: "Designed for non-technical learners" },
  { icon: Shield, text: "Focused on confidence, safety, and productivity" },
  { icon: BookOpen, text: "Clear teaching with relevant examples" },
  { icon: Briefcase, text: "Suitable for professionals, teams, and businesses" },
  { icon: Lightbulb, text: "A clean, practical approach without unnecessary complexity" },
];

export default function HomePage() {
  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate("/contact");
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="bg-white py-12 md:py-16 lg:py-20" data-testid="hero-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <AnimatedSection>
              <div className="max-w-xl">
                <h1 className="heading-display text-[#0A192F] mb-6 font-['Manrope']">
                  Learn AI Skills for the Way the World Now Works
                </h1>
                <p className="text-body-lg mb-5">
                  Be AI Ready helps professionals and businesses learn how to use AI 
                  confidently, safely, and productively through practical workshops and training.
                </p>
                <p className="text-body mb-8">
                  As AI changes the way the world works, those who learn to use it well 
                  will be better prepared to stay competitive — and those who ignore it 
                  risk falling behind.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    onClick={scrollToContact}
                    className="bg-[#0A192F] hover:bg-[#112240] text-white font-semibold px-7 py-6 text-[15px] shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5"
                    data-testid="hero-enquire-btn"
                  >
                    Enquire About a Workshop
                    <ArrowRight size={18} />
                  </Button>
                  <Button
                    onClick={scrollToContact}
                    variant="outline"
                    className="border-2 border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white font-semibold px-7 py-6 text-[15px] transition-all hover:-translate-y-0.5"
                    data-testid="hero-discovery-btn"
                  >
                    Book a Discovery Call
                  </Button>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="hero-image-wrapper">
                <img
                  src={HERO_IMAGE}
                  alt="Professional confidently working with AI tools in a modern Australian office"
                  className="w-full h-auto rounded-xl shadow-lg object-cover aspect-[4/3]"
                  data-testid="hero-image"
                  loading="eager"
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="trust-strip py-6 md:py-8" data-testid="trust-strip">
        <div className="container-main">
          <AnimatedSection>
            <p className="text-center text-base md:text-lg text-[#0A192F] font-medium font-['Manrope'] tracking-tight">
              No hype. No technical overwhelm. Just practical AI education for professionals and businesses.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Why Learning AI Matters Now */}
      <section className="bg-white section-spacing" data-testid="why-ai-matters-section">
        <div className="container-main">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto mb-12 md:mb-14">
              <h2 className="heading-section text-[#0A192F] mb-5 font-['Manrope']">
                Why learning AI matters now
              </h2>
              <p className="text-body-lg">
                AI is changing how people work, communicate, learn, research, and solve problems. 
                Learning how to use AI well is becoming an increasingly valuable skill across many 
                roles and industries.
              </p>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-4xl mx-auto">
            {whyAIMatters.map((item, index) => (
              <AnimatedSection key={index} delay={index * 0.03}>
                <div className="flex items-start gap-3 p-4 rounded-lg hover:bg-[#F8FAFC] transition-colors">
                  <CheckCircle2 className="text-[#008A90] flex-shrink-0 mt-0.5" size={20} />
                  <p className="text-body-sm">{item}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-[#F8FAFC] section-spacing" data-testid="benefits-section">
        <div className="container-main">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto mb-12 md:mb-14">
              <h2 className="heading-section text-[#0A192F] mb-5 font-['Manrope']">
                The real benefits of learning and using AI
              </h2>
              <p className="text-body-lg">
                Discover how AI can make a genuine difference in your work and productivity.
              </p>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((benefit, index) => (
              <AnimatedSection key={index} delay={index * 0.05}>
                <div className="feature-card h-full">
                  <div className="icon-wrapper mb-4">
                    <benefit.icon size={22} />
                  </div>
                  <p className="text-body">{benefit.text}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* How AI Can Help */}
      <section className="bg-white section-spacing" data-testid="how-ai-helps-section">
        <div className="container-main">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto mb-12 md:mb-14">
              <h2 className="heading-section text-[#0A192F] mb-5 font-['Manrope']">
                How AI can help in real work
              </h2>
              <p className="text-body-lg">
                Practical outcomes you can achieve with AI skills.
              </p>
            </div>
          </AnimatedSection>
          
          {/* Quick outcomes grid */}
          <div className="flex flex-wrap justify-center gap-3 mb-14">
            {howAIHelps.map((item, index) => (
              <AnimatedSection key={index} delay={index * 0.03}>
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#E2E8F0] bg-white hover:border-[#008A90] hover:bg-[#F0FDFA] transition-all cursor-default">
                  <item.icon className="text-[#008A90]" size={18} />
                  <span className="text-sm font-medium text-[#334155]">{item.text}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* By audience */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {audienceExamples.map((audience, index) => (
              <AnimatedSection key={index} delay={index * 0.08}>
                <div className="feature-card h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="icon-wrapper">
                      <audience.icon size={20} />
                    </div>
                    <h3 className="font-semibold text-[#0A192F] font-['Manrope'] text-base">
                      {audience.title}
                    </h3>
                  </div>
                  <ul className="space-y-2.5">
                    {audience.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-body-sm">
                        <CheckCircle2 size={14} className="text-[#008A90] mt-1 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Workshops Section */}
      <section className="bg-[#F8FAFC] section-spacing" data-testid="workshops-section">
        <div className="container-main">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto mb-12 md:mb-14">
              <h2 className="heading-section text-[#0A192F] mb-5 font-['Manrope']">
                Our Workshops
              </h2>
              <p className="text-body-lg">
                Choose the workshop that fits your needs and learning goals.
              </p>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {workshops.map((workshop, index) => (
              <AnimatedSection key={index} delay={index * 0.12}>
                <WorkshopCard {...workshop} />
              </AnimatedSection>
            ))}
          </div>
          <AnimatedSection delay={0.25}>
            <div className="text-center mt-10">
              <Link to="/workshops">
                <Button
                  variant="outline"
                  className="border-2 border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white font-semibold px-7 py-5 transition-all hover:-translate-y-0.5"
                  data-testid="view-all-workshops-btn"
                >
                  View All Workshop Details
                  <ArrowRight size={16} />
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-white section-spacing" data-testid="why-choose-us-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <AnimatedSection>
              <div className="relative">
                <img
                  src={WORKSHOP_IMAGE}
                  alt="Team of Australian professionals participating in an AI training workshop"
                  className="w-full h-auto rounded-xl shadow-lg object-cover aspect-[4/3]"
                  data-testid="why-choose-us-image"
                  loading="lazy"
                />
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.15}>
              <h2 className="heading-section text-[#0A192F] mb-8 font-['Manrope']">
                Why choose Be AI Ready
              </h2>
              <div className="space-y-5">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="icon-wrapper flex-shrink-0">
                      <item.icon size={20} />
                    </div>
                    <p className="text-body-lg pt-2">{item.text}</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* About Intro */}
      <section className="page-hero text-white section-spacing" data-testid="about-intro-section">
        <div className="container-main">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center">
              <Sparkles className="mx-auto mb-5 text-[#008A90]" size={36} />
              <h2 className="heading-section text-white mb-5 font-['Manrope']">
                About Be AI Ready
              </h2>
              <p className="text-lg text-[#94A3B8] leading-relaxed mb-8">
                Be AI Ready was created to help people and businesses understand and use AI 
                in a practical way. The focus is on building confidence, improving productivity, 
                and helping learners apply AI in real work without unnecessary complexity.
              </p>
              <Link to="/about">
                <Button
                  className="bg-[#008A90] hover:bg-[#007075] text-white font-semibold px-7 py-5 transition-all hover:-translate-y-0.5"
                  data-testid="learn-more-about-btn"
                >
                  Learn More About Us
                  <ArrowRight size={16} />
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white section-spacing" data-testid="final-cta-section">
        <div className="container-main">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="heading-section text-[#0A192F] mb-5 font-['Manrope']">
                Ready to become AI ready?
              </h2>
              <p className="text-body-lg mb-8">
                Get in touch to discuss the right workshop for you, your team, or your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={scrollToContact}
                  className="bg-[#0A192F] hover:bg-[#112240] text-white font-semibold px-7 py-6 text-[15px] shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5"
                  data-testid="final-cta-enquire-btn"
                >
                  Enquire About a Workshop
                  <ArrowRight size={18} />
                </Button>
                <Button
                  onClick={scrollToContact}
                  variant="outline"
                  className="border-2 border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white font-semibold px-7 py-6 text-[15px] transition-all hover:-translate-y-0.5"
                  data-testid="final-cta-discovery-btn"
                >
                  Book a Discovery Call
                </Button>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
